import { useState } from 'react'
import { Button } from './Button'
import { Modal } from './Modal'

export interface ConfirmDialogProps {
  readonly open: boolean
  readonly title: string
  readonly description: string
  readonly confirmLabel: string
  readonly onCancel: () => void
  readonly onConfirm: () => Promise<void>
  /** UID-140 code-review follow-up: a caller-owned error (e.g. a rejected
   * delete/confirm mutation) surfaced INSIDE the still-open dialog, rather
   * than the caller rendering it in a body area the modal visually covers.
   * Omitted/undefined renders nothing — every existing call site is
   * unaffected. */
  readonly error?: string | undefined
}

/**
 * Shared destructive-action confirmation (Delete a saved address / Remove a
 * saved payment method, UID-97 K2/K3) — built on the shared `Modal` shell so
 * it inherits the same focus-trap/Escape/backdrop-dismiss/focus-return
 * behavior as every other modal in the app, per the accessibility-wcag
 * skill's requirement that destructive actions get an explicit,
 * keyboard-operable confirmation rather than an inline no-undo click.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  onCancel,
  onConfirm,
  error,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false)

  async function handleConfirm() {
    setBusy(true)
    try {
      await onConfirm()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      maxWidth={440}
      footer={
        <>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => void handleConfirm()} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p>{description}</p>
      {error && (
        <p role="alert" className="confirm-dialog-error">
          {error}
        </p>
      )}
    </Modal>
  )
}
